import { useAppForm } from '@/lib/form'
import { useSyncNotificationEventRecipientsMutation } from '@/lib/services/notification-events/notification-events.query'
import { getErrorMessage } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import { useRef, useState, type RefObject } from 'react'
import type {
  NotificationChannel,
  NotificationEvent,
} from '@/lib/services/notification-events/notification-events.types'
import type { NotificationRecipient } from '@/lib/services/notification-recipients/notification-recipients.types'
import { toast } from 'sonner'
import {
  buildNotificationEventFormValues,
  getChangedNotificationEvents,
  normalizeEventRecipientMap,
  normalizeRecipientIds,
  type NotificationEventRecipientMap,
} from './-notification-event-subscriptions'

type NotificationSubscriptionEvent = Pick<NotificationEvent, 'id' | 'recipient_ids'>
type NotificationSubscriptionFormValues = { eventRecipientIds: NotificationEventRecipientMap }

type NotificationSubscriptionFormControls = {
  reset: (values: NotificationSubscriptionFormValues) => void
  setFieldValue: (
    name: 'eventRecipientIds',
    updater: (current: NotificationEventRecipientMap) => NotificationEventRecipientMap
  ) => void
}

type NotificationSubscriptionBaseline = {
  formApi: NotificationSubscriptionFormControls
  nextEventRecipientIds: NotificationEventRecipientMap
  baselineEventRecipientIdsRef: RefObject<NotificationEventRecipientMap>
  setDefaultEventRecipientIds: (value: NotificationEventRecipientMap) => void
}

type NotificationEventSubscriptionsFormInput = {
  enabledRecipientIds: ReadonlySet<number>
  notificationEvents: NotificationSubscriptionEvent[]
  recipientsByChannel: Partial<Record<NotificationChannel, Pick<NotificationRecipient, 'id'>[]>>
}

export function useNotificationEventSubscriptionsForm({
  enabledRecipientIds,
  notificationEvents,
  recipientsByChannel,
}: NotificationEventSubscriptionsFormInput) {
  const syncRecipientsMutation = useSyncNotificationEventRecipientsMutation()
  const [defaultEventRecipientIds, setDefaultEventRecipientIds] = useState(
    () =>
      buildNotificationEventFormValues(notificationEvents, enabledRecipientIds).eventRecipientIds
  )
  const baselineEventRecipientIdsRef = useRef(defaultEventRecipientIds)

  const form = useAppForm({
    defaultValues: {
      eventRecipientIds: defaultEventRecipientIds,
    },
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        const nextEventRecipientIds = normalizeEventRecipientMap(
          value.eventRecipientIds,
          enabledRecipientIds
        )
        const baselineEventRecipientIds = normalizeEventRecipientMap(
          baselineEventRecipientIdsRef.current,
          enabledRecipientIds
        )
        const nextFormValues = {
          ...value,
          eventRecipientIds: nextEventRecipientIds,
        }
        const changedEvents = getChangedNotificationEvents(
          notificationEvents,
          baselineEventRecipientIds,
          nextEventRecipientIds
        )

        if (!changedEvents.length) {
          resetSubscriptionBaseline({
            formApi,
            nextEventRecipientIds,
            nextFormValues,
            baselineEventRecipientIdsRef,
            setDefaultEventRecipientIds,
          })
          return
        }

        const mutationResults = await Promise.allSettled(
          changedEvents.map((event) =>
            syncRecipientsMutation.mutateAsync({
              eventId: event.id,
              recipientIds: nextEventRecipientIds[event.id] ?? [],
            })
          )
        )

        const successfulEventIds = changedEvents
          .filter((_, index) => mutationResults[index]?.status === 'fulfilled')
          .map((event) => event.id)
        const failedEvents = changedEvents.filter(
          (_, index) => mutationResults[index]?.status === 'rejected'
        )

        if (!failedEvents.length) {
          resetSubscriptionBaseline({
            formApi,
            nextEventRecipientIds,
            nextFormValues,
            baselineEventRecipientIdsRef,
            setDefaultEventRecipientIds,
          })
          toast.success('Notification subscriptions updated')
          return
        }

        if (successfulEventIds.length) {
          resetSuccessfulSubscriptionBaseline({
            formApi,
            failedEvents,
            nextEventRecipientIds,
            baselineEventRecipientIdsRef,
            setDefaultEventRecipientIds,
            successfulEventIds,
          })
        }

        const firstError: unknown = mutationResults.find(
          (result) => result.status === 'rejected'
        )?.reason

        if (successfulEventIds.length) {
          toast.error('Some notification subscriptions failed to update', {
            description: getErrorMessage(firstError),
          })
        } else {
          toast.error('Failed to update notification subscriptions', {
            description: getErrorMessage(firstError),
          })
        }
      },
    },
  })
  const eventRecipientIds = useStore(form.store, (state) => state.values.eventRecipientIds)

  const toggleRecipient = (eventId: number, recipientId: number, checked: boolean) => {
    form.setFieldValue('eventRecipientIds', (current) => {
      const currentRecipientIds = current[eventId] ?? []
      const nextRecipientIds = checked
        ? [...currentRecipientIds, recipientId]
        : currentRecipientIds.filter((id) => id !== recipientId)

      return {
        ...current,
        [eventId]: normalizeRecipientIds(nextRecipientIds),
      }
    })
  }

  const toggleChannel = (eventId: number, channel: NotificationChannel, checked: boolean) => {
    const channelRecipientIds = (recipientsByChannel[channel] ?? []).map(
      (recipient) => recipient.id
    )

    form.setFieldValue('eventRecipientIds', (current) => {
      const currentRecipientIds = current[eventId] ?? []
      const nonChannelRecipientIds = currentRecipientIds.filter(
        (id) => !channelRecipientIds.includes(id)
      )
      const nextRecipientIds = checked
        ? [...nonChannelRecipientIds, ...channelRecipientIds]
        : nonChannelRecipientIds

      return {
        ...current,
        [eventId]: normalizeRecipientIds(nextRecipientIds),
      }
    })
  }

  const getChannelCheckedState = (
    eventId: number,
    channel: NotificationChannel
  ): boolean | 'indeterminate' => {
    const channelRecipientIds = (recipientsByChannel[channel] ?? []).map(
      (recipient) => recipient.id
    )
    const selectedRecipientIds = eventRecipientIds[eventId] ?? []

    if (channelRecipientIds.length === 0) {
      return false
    }

    const selectedCount = channelRecipientIds.filter((id) =>
      selectedRecipientIds.includes(id)
    ).length

    if (selectedCount === 0) {
      return false
    }

    if (selectedCount === channelRecipientIds.length) {
      return true
    }

    return 'indeterminate'
  }

  return {
    eventRecipientIds,
    form,
    getChannelCheckedState,
    syncRecipientsMutation,
    toggleChannel,
    toggleRecipient,
  }
}

function resetSubscriptionBaseline({
  baselineEventRecipientIdsRef,
  formApi,
  nextEventRecipientIds,
  nextFormValues,
  setDefaultEventRecipientIds,
}: NotificationSubscriptionBaseline & { nextFormValues: NotificationSubscriptionFormValues }) {
  baselineEventRecipientIdsRef.current = nextEventRecipientIds
  setDefaultEventRecipientIds(nextEventRecipientIds)
  formApi.reset(nextFormValues)
}

function resetSuccessfulSubscriptionBaseline({
  baselineEventRecipientIdsRef,
  failedEvents,
  formApi,
  nextEventRecipientIds,
  setDefaultEventRecipientIds,
  successfulEventIds,
}: NotificationSubscriptionBaseline & {
  failedEvents: NotificationSubscriptionEvent[]
  successfulEventIds: number[]
}) {
  const nextBaseline = {
    ...baselineEventRecipientIdsRef.current,
  }

  successfulEventIds.forEach((eventId) => {
    nextBaseline[eventId] = nextEventRecipientIds[eventId] ?? []
  })

  baselineEventRecipientIdsRef.current = normalizeEventRecipientMap(nextBaseline)
  setDefaultEventRecipientIds(baselineEventRecipientIdsRef.current)
  formApi.reset({ eventRecipientIds: baselineEventRecipientIdsRef.current })
  formApi.setFieldValue('eventRecipientIds', (current) => ({
    ...current,
    ...Object.fromEntries(
      failedEvents.map((event) => [event.id, nextEventRecipientIds[event.id] ?? []])
    ),
  }))
}
